import React, { useCallback, useEffect, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import debounce from '../../shared/debounce';
import squares from '../../assets/images/squares.svg';
import { Icon } from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';
import {
  CatalogItemDragPayload,
  MARKETING_COLLECTION_ITEM_DRAG_TYPE,
} from '../CollectionCatalogDnD/CollectionCatalogDnD';

type CatalogItemsDraggableListProps<T extends { id?: number }> = {
  items: T[];
  sourceCollectionId: number;
  disabled?: boolean;
  dragHandleTitle: string;
  onMovedItem?: (reorderedItems: T[]) => void;
  toElementFn: (element: T, index: number) => JSX.Element;
  emptyListComponent?: JSX.Element | (() => JSX.Element);
};

type SortableRowProps<T extends { id?: number }> = {
  item: T;
  index: number;
  collectionId: number;
  disabled: boolean;
  dragHandleTitle: string;
  setOrderedItems: React.Dispatch<React.SetStateAction<T[]>>;
  debouncedOnMovedItem: (items: T[]) => void;
  toElementFn: (element: T, index: number) => JSX.Element;
};

function CatalogItemSortableRow<T extends { id?: number }>({
  item,
  index,
  collectionId,
  disabled,
  dragHandleTitle,
  setOrderedItems,
  debouncedOnMovedItem,
  toElementFn,
}: SortableRowProps<T>) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: MARKETING_COLLECTION_ITEM_DRAG_TYPE,
      item: (): CatalogItemDragPayload => ({
        itemId: item.id!,
        sourceCollectionId: collectionId,
        index,
      }),
      canDrag: !disabled && item.id != null,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [item, index, collectionId, disabled],
  );

  const [, drop] = useDrop(
    () => ({
      accept: MARKETING_COLLECTION_ITEM_DRAG_TYPE,
      canDrop: (dragged: CatalogItemDragPayload) =>
        dragged.sourceCollectionId === collectionId && dragged.index !== index,
      hover: (dragged: CatalogItemDragPayload) => {
        if (dragged.sourceCollectionId !== collectionId) {
          return;
        }
        const dragIndex = dragged.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) {
          return;
        }
        setOrderedItems((prev) => {
          const next = [...prev];
          const [removed] = next.splice(dragIndex, 1);
          next.splice(hoverIndex, 0, removed);
          debouncedOnMovedItem(next);
          return next;
        });
        dragged.index = hoverIndex;
      },
    }),
    [collectionId, index, setOrderedItems, debouncedOnMovedItem],
  );

  return (
    <div
      ref={drop}
      className={`flex w-full items-center space-x-2 ${isDragging ? 'opacity-60' : ''}`}
    >
      <Tooltip content={dragHandleTitle}>
        <span
          ref={drag}
          className={`reorder-handle inline-flex flex-shrink-0 ${
            disabled || item.id == null ? '' : 'cursor-grab active:cursor-grabbing'
          }`}
        >
          <Icon
            url={squares}
            cssStyles={`w-5 pointer-events-none ${disabled || item.id == null ? 'opacity-40' : ''}`}
          />
        </span>
      </Tooltip>
      <div className="flex-1 min-w-0">{toElementFn(item, index)}</div>
    </div>
  );
}

function CatalogItemsDraggableList<T extends { id?: number }>({
  items,
  sourceCollectionId,
  disabled = false,
  dragHandleTitle,
  onMovedItem,
  toElementFn,
  emptyListComponent,
}: CatalogItemsDraggableListProps<T>) {
  const [orderedItems, setOrderedItems] = useState<T[]>(items);

  useEffect(() => {
    setOrderedItems(items);
  }, [items]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnMovedItem = useCallback(
    debounce((next: T[]) => {
      onMovedItem?.(next);
    }, 1500),
    [],
  );

  if (!orderedItems.length && emptyListComponent) {
    return <>{typeof emptyListComponent === 'function' ? emptyListComponent() : emptyListComponent}</>;
  }

  return (
    <div className="flex flex-col flex-1 space-y-2">
      {orderedItems.map((el, i) => (
        <CatalogItemSortableRow<T>
          key={el.id ?? i}
          item={el}
          index={i}
          collectionId={sourceCollectionId}
          disabled={disabled}
          dragHandleTitle={dragHandleTitle}
          setOrderedItems={setOrderedItems}
          debouncedOnMovedItem={debouncedOnMovedItem}
          toElementFn={toElementFn}
        />
      ))}
    </div>
  );
}

export default CatalogItemsDraggableList;
